/**
 * O parâmetro de uma ação que tem uma lista fechada (a etapa, a pessoa da
 * atribuição, o motivo de perda): escolhe-se o valor da lista que o CRM mandou,
 * sem escrever o id entre aspas. A pessoa da atribuição tem também "Ninguém",
 * que manda `null`.
 *
 * A expressão continua possível ("Usar uma expressão"): a etapa pode vir do
 * evento. A que não é um valor da lista abre como texto, como está.
 */

import { useState, type ReactNode } from 'react';
import { expressaoDoValor, valorFixo } from '@/shared/lib/valor-fixo';
import { Botao, Campo, CampoSelecao } from '@/shared/ui';
import { ComCamposDoEvento } from './inserir-campo';

/** O valor da escolha "Ninguém" no seletor; na expressão, ela é `null`. */
const NINGUEM = '\u0000ninguem';
/** O prefixo das escolhas que vêm do evento: o valor delas é o caminho, não um texto. */
const DO_EVENTO = '\u0000evento:';

export function ParametroComOpcoes({
  rotulo,
  opcoes,
  opcaoNula,
  vazio,
  seVazia,
  doEvento = [],
  campos,
  valor,
  aoMudar,
}: {
  rotulo: string;
  opcoes: readonly { valor: string; rotulo: string }[];
  /** O nome da escolha que manda `null`, quando a ação a aceita. */
  opcaoNula?: string | undefined;
  /** O nome da escolha vazia ("Sem motivo"); sem ele, "Escolha". */
  vazio?: string | undefined;
  /** O aviso quando a lista da organização vem vazia: onde cadastrar. */
  seVazia?: string | undefined;
  /** O que o evento traz deste tipo ("O novo responsável"): vai como caminho. */
  doEvento?: readonly { caminho: string; titulo: string }[];
  /** Os nomes do evento para o "Inserir" da expressão. */
  campos: readonly string[];
  valor: string;
  aoMudar: (valor: string) => void;
}): ReactNode {
  const fixo = valorFixo(valor);
  const doEventoEscolhido = doEvento.find((r) => r.caminho === valor.trim());
  const escolhido = doEventoEscolhido
    ? `${DO_EVENTO}${doEventoEscolhido.caminho}`
    : fixo === null && opcaoNula
      ? NINGUEM
      : typeof fixo === 'string' && opcoes.some((o) => o.valor === fixo)
        ? fixo
        : undefined;
  const daLista = valor.trim() === '' || escolhido !== undefined;
  const [pediuExpressao, definirPediuExpressao] = useState(false);
  /*
   * A lista vazia com o aviso dela continua uma escolha: o opcional fica no
   * "Sem motivo", e o aviso diz onde cadastrar, em vez de pedir uma expressão.
   */
  const temLista =
    opcoes.length > 0 || Boolean(opcaoNula) || doEvento.length > 0 || Boolean(seVazia);

  if (temLista && daLista && !pediuExpressao) {
    return (
      <div className="editor-do-mapa__condicao">
        <CampoSelecao
          rotulo={rotulo}
          {...(opcoes.length === 0 && seVazia ? { ajuda: seVazia } : {})}
          value={escolhido ?? ''}
          onChange={(e) =>
            aoMudar(
              e.target.value.startsWith(DO_EVENTO)
                ? e.target.value.slice(DO_EVENTO.length)
                : e.target.value === NINGUEM
                  ? expressaoDoValor(null)
                  : e.target.value
                    ? expressaoDoValor(e.target.value)
                    : '',
            )
          }
        >
          <option value="">{vazio ?? 'Escolha'}</option>
          {opcaoNula && <option value={NINGUEM}>{opcaoNula}</option>}
          {doEvento.length > 0 && (
            <optgroup label="Do evento">
              {doEvento.map((r) => (
                <option key={r.caminho} value={`${DO_EVENTO}${r.caminho}`}>
                  {r.titulo}
                </option>
              ))}
            </optgroup>
          )}
          {doEvento.length > 0 && opcoes.length > 0 ? (
            <optgroup label="Da equipe">
              {opcoes.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </optgroup>
          ) : (
            opcoes.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.rotulo}
              </option>
            ))
          )}
        </CampoSelecao>
        <Botao variante="fantasma" miudo onClick={() => definirPediuExpressao(true)}>
          Usar uma expressão
        </Botao>
      </div>
    );
  }

  return (
    <div className="editor-do-mapa__condicao">
      <ComCamposDoEvento
        campos={campos}
        valor={valor}
        aoMudar={(v) => {
          definirPediuExpressao(true);
          aoMudar(v);
        }}
      >
        {(ref) => (
          <Campo
            ref={ref}
            rotulo={rotulo}
            {...(temLista
              ? {}
              : { ajuda: 'Não há uma lista para escolher aqui: escreva a expressão.' })}
            spellCheck={false}
            value={valor}
            onChange={(e) => {
              definirPediuExpressao(true);
              aoMudar(e.target.value);
            }}
          />
        )}
      </ComCamposDoEvento>
      {temLista && (
        <Botao
          variante="fantasma"
          miudo
          onClick={() => {
            definirPediuExpressao(false);
            if (escolhido === undefined) aoMudar('');
          }}
        >
          Escolher da lista
        </Botao>
      )}
    </div>
  );
}
